import Vue from 'vue';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { modals } from '@/mixins/modal';
import { guid } from '@/mixins/guid';
import { editHistory } from '@/mixins/history';
import { recycleDroplet } from '@/mixins/recycle-droplet';
import { siteBuilder } from '@/mixins/site-builder';
import { removeDropletHook } from '@/mixins/remove-droplet';
import { cssGenerator } from '@/mixins/css-generator';
import { queuedWork } from '@/mixins/queued-work';
import tooltip from '@/mixins/components/tooltip';
import { SQUASH_DROPLET } from '@/constants';
import { diff } from 'deep-object-diff';
import sectionDefaults from '@/payloads/section';
import SectionDroplets from './components/section-droplets';
import EditSection from './components/edit-options';
import RecycleSection from './components/recycle-modal';
import ConfirmModal from './components/confirm-modal';
import ErrorModal from './components/error-modal';
import PointupDivider from './components/shape-dividers/point-up';
import SlantrightDivider from './components/shape-dividers/slant-right';
import SlantleftDivider from './components/shape-dividers/slant-left';
import CurveDivider from './components/shape-dividers/curve';
import WaveoneDivider from './components/shape-dividers/wave-one';
import WavetwoDivider from './components/shape-dividers/wave-two';
import CustomDivider from './components/shape-dividers/custom';

export default Vue.component('section-block', {
  template: require('./template.html'),
  components: {
    SectionDroplets,
    EditSection,
    RecycleSection,
    ConfirmModal,
    ErrorModal,
    PointupDivider,
    SlantrightDivider,
    SlantleftDivider,
    CurveDivider,
    WavetwoDivider,
    CustomDivider,
    tooltip,
  },
  mixins: [
    modals,
    guid,
    cssGenerator,
    recycleDroplet,
    siteBuilder,
    removeDropletHook,
    editHistory,
    queuedWork,
  ],
  data() {
    return {
      loading: false,
      showEditBubble: false,
      dropletName: 'section-block',
      dropletId: 1,
      showRecycleModal: false,
      showConfirmModal: false,
      showErrorModal: false,
      showSaveModal: false,
      sectionHovered: false,
      showSectionBtns: false,
      showTemplateTip: false,
      helperTimeout: null,
      dragging: false,
      height: null,
      section: null,
      sectionHeight: null,
      startY: null,
      zIndex: 3,
      hidden: false,
      setBubbleSize: 'dropletBubble-normal',
      pluginDeb: null,
      SQUASH_DROPLET,
      lastRowSelector:
        '> .container > .row:last-child, > .container > .row:last-of-type, > .container > .row:only-child, > .container > .row:only-of-type',
      display: {
        'margin-bottom': null,
        'padding-top': null,
        'padding-bottom': null,
        'padding-left': null,
        'padding-right': null,
        'border-left-width': null,
        'border-right-width': null,
        'border-top-width': null,
        'border-bottom-width': null,
        'min-height': null,
        'box-shadow': null,
        transform: null,
        top: null,
        bottom: null,
      },
      videoDefaults: {
        desktop: { src: false, type: false, inherit: true },
        tablet: { src: false, type: false, inherit: true },
        mobile: { src: false, type: false, inherit: true },
      },
      ...sectionDefaults,
    };
  },
  name: 'section-block',
  props: [
    'block-index',
    'droplet',
    'items',
    'index',
    'canvas',
    'descendants',
  ],
  computed: {
    ...mapGetters([
      'getContentBlocks',
      'currentPage',
      'initialCurrentPage',
      'viewport',
      'dropletObserver',
      'localStyle',
      'activeDroplet',
      'bouncePluginHook',
    ]),
    profile() {
      return this._dropletType(this.droplet);
    },
    dividers() {
      return {
        pointup: PointupDivider,
        slantright: SlantrightDivider,
        slantleft: SlantleftDivider,
        curve: CurveDivider,
        waveone: WaveoneDivider,
        wavetwo: WavetwoDivider,
        custom: CustomDivider,
      };
    },
    canvasEditor() {
      return _.isEqual(this.currentPage, this.droplet);
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ' ' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    videoMediaQueries() {
      const videoBg = this.droplet.videoBg;
      return JSON.stringify(videoBg);
    },
    shapeStyles() {
      const selector = _.find(this.droplet.mediaQueries, {
        selector: '.drz-divider-shape',
      });
      const property = selector ? _.find(selector.properties, { property: 'fill' }) : false;
      return property;
    },
    lastRowMargin() {
      const selector = _.find(this.droplet.mediaQueries, {
        selector: this.lastRowSelector,
      });
      const property = selector
        ? _.find(selector.properties, { property: 'margin-bottom' })
        : false;
      return property;
    },
    shapeData() {
      const coords = this.droplet.shapeDividerCoords;
      return {
        type: coords ? coords.type : 'pointup',
        location: coords ? coords.location : 'bottom',
        customFile: coords ? coords.customFile : '',
      };
    },
  },
  created() {
    this.checkNewValues();
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    blockIndex() {
      this.bounceVideo();
    },
    viewport() {
      this.callCssGenerator();
      this.observeVisibility();
      this.bounceVideo();
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
    bouncePluginHook(id) {
      if (this.droplet._id === id) {
        clearTimeout(this.pluginDeb);
        this.pluginDeb = setTimeout(() => {
          this.bounceVideo();
          this.$store.dispatch('callPluginBounce', false);
        }, 500);
      }
    },
    showRecycleModal(open) {
      this.$store.dispatch('setGlobalOpenModal', open);
    },
    showConfirmModal(open) {
      this.$store.dispatch('setGlobalOpenModal', open);
    },
    showErrorModal(open) {
      this.$store.dispatch('setGlobalOpenModal', open);
    },
    showSaveModal(open) {
      this.$store.dispatch('setGlobalOpenModal', open);
    },
  },
  mounted() {
    this.callCssGenerator();
    this.observeVisibility();
    this.bounceVideo();
  },
  beforeDestroy() {
    const $video = $(this.$refs.sectionVideo);
    if ($video.drzSectionVideo.destroy) {
      $video.drzSectionVideo.destroy($video);
    }
  },
  methods: {
    changeBubbleSize(size) {
      this.setBubbleSize = `dropletBubble-${size}`;
    },
    callCssGenerator() {
      return this.generateCSS({
        drp: this.droplet,
      });
    },
    checkNewValues() {
      const values = [];
      if (!this.droplet.hasVideo) {
        values.push({ key: 'hasVideo', value: false });
      }
      if (!this.droplet.videoBg) {
        values.push({ key: 'videoBg', value: _.cloneDeep(this.videoDefaults) });
      }
      if (!this.droplet.sectionErrors) {
        values.push({ key: 'sectionErrors', value: { id: false } });
      }
      const shapeCoords = this.droplet.shapeDividerCoords;
      if (!this.keyIsSet(shapeCoords)) {
        values.push({
          key: 'shapeDividerCoords',
          value: {
            type: 'pointup',
            location: 'bottom',
          },
        });
      } else if (!shapeCoords.type || !shapeCoords.location) {
        this.$store.dispatch('setNewDropletValues', {
          drp: shapeCoords,
          vm: this,
          values: [{ key: 'type', value: 'pointup' }, { key: 'location', value: 'bottom' }],
        });
      }
      // check for media queries
      if (!this.shapeStyles) {
        this.$store.dispatch('changeDropletCss', {
          drp: this.droplet,
          val: 'rgba(255, 255, 255, 1)',
          property: 'fill',
          selector: '.drz-divider-shape',
          viewport: this.viewport,
        });
        this.callCssGenerator();
      }
      if (!this.lastRowMargin) {
        this.$store.dispatch('changeDropletCss', {
          drp: this.droplet,
          val: '0px',
          property: 'margin-bottom',
          selector: this.lastRowSelector,
          viewport: this.viewport,
        });
        this.callCssGenerator();
      }
      if (!this.keyIsSet(this.droplet.shapeDivider)) {
        values.push({ key: 'shapeDivider', value: false });
      }
      if (values.length > 0) {
        this.$store.dispatch('setNewDropletValues', {
          drp: this.droplet,
          vm: this,
          values,
        });
      }
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    keyIsSet(prop) {
      return typeof prop !== 'undefined';
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    bounceVideo() {
      const $video = $(this.$refs.sectionVideo);
      if ($video.drzSectionVideo.destroy) {
        $video.drzSectionVideo.destroy($video);
      }
      $video.drzSectionVideo();
    },
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    addSection() {
      this.$emit('add-block', this.blockIndex);
    },
    hoverSectionOptions() {
      this.zIndex = 20;
      this.$store.dispatch('setGlobalOptionsHover', true);
    },
    offSectionOptions() {
      this.showSectionBtns = false;
      this.showTemplateTip = false;
      this.zIndex = 3;
      this.$store.dispatch('setGlobalOptionsHover', false);
    },
    async goToTemplate() {
      const cP = this._deepOmit(this.currentPage, this._pageDiffIgnore);
      const iP = this._deepOmit(this.initialCurrentPage, this._pageDiffIgnore);
      if (_.isEqual(cP, iP)) {
        await this.setCurrentPage();
      } else {
        // unsaved changes to page
        this.toggleModal('Save');
        this.$log.info('Unsaved Page Difference: ', diff(cP, iP));
      }
    },
    async setCurrentPage() {
      const template = _.find(this._currentSite.templates, {
        _id: this.currentPage.templateID,
      });
      const { item } = await this.checkQueuedWork({ page: template });
      this.$store.dispatch('setOriginalPage', item);
      return this.$store.dispatch('setCurrentPage', item);
    },
    async checkPage(choice) {
      // This should never run on a template or a recycled droplet, only pages and drafts
      // since this is triggered when clicking on a template droplet in a page or draft
      if (choice === 'yes') {
        // revert the droplet count to the initial value
        await this.$store.dispatch('revertDropletCounts', {
          site: this.currentSite,
        });
        // revert all page data to the initial value
        await this.$store.dispatch('revertPage', this.initialCurrentPage);
        this.$store.dispatch('updateInitialPage');
        this.toggleModal('Save');
        this.setCurrentPage();
      }
      if (choice === 'no') {
        this.toggleModal('Save');
      }
    },
    toggleModal(type) {
      const modal = `show${type}Modal`;
      this[modal] = !this[modal];
      const content = this.$refs[`drp${type}Modal`];
      if (this[modal]) {
        this.modelMoveOut(content);
        this.showEditBubble = false;
        this.$store.dispatch('setActiveDroplet', { droplet: {}, items: [] });
      } else {
        this.modalMoveIn(content);
      }
    },
    toggleBubble() {
      this.$store.dispatch('setActiveDroplet', { droplet: {}, items: [] });
      this.showEditBubble = !this.showEditBubble;
    },
    toggleSideBar() {
      if (this.showEditBubble) {
        this.showEditBubble = false;
      }
      const droplet = !this.activeDroplet._id ? this.droplet : {};
      this.$store.dispatch('setActiveDroplet', {
        droplet,
        items: this.items,
        index: this.index,
      });
    },
    startResizeDrag(e) {
      if (!this.dragging) {
        this.startY = e.clientY;
        this.section = this.$el;
        this.display['min-height'] = ~~this.section.offsetHeight;
        e.dataTransfer.setData('text/plain', e.target.id);
        document.addEventListener('dragover', this.commenceResizing, false);
        this.dragging = true;
      }
    },
    endResizeDrag() {
      if (this.dragging) {
        const payload = {};
        payload.drp = this.droplet;
        payload.selector = ' ';
        payload.property = 'min-height';
        payload.val = `${this.height}px`;
        payload.viewport = this.viewport;
        this.dragging = false;
        document.removeEventListener('dragover', this.commenceResizing, false);
        this.checkLegacyStyles('min-height');
        this.$store.dispatch('changeDropletCss', payload);
        this.callCssGenerator();
        this.height = null;
        this.display['min-height'] = null;
        this.$store.dispatch('setSiteSave', { status: true, type: 'Section' });
        this.setEditHistory({
          type: 'resize_section',
          meta: {
            dropletType: this.droplet.type,
          },
        });
      }
    },
    commenceResizing(e) {
      if (!this.dragging) {
        return;
      }
      const pos = e.clientY;
      if (pos > 0) {
        const newH = parseInt(this.display['min-height'] + (pos - this.startY), 10);
        if (newH >= 0) {
          this.height = newH;
        }
      }
    },
    checkLegacyStyles(property) {
      const i = _.findIndex(this.droplet.mediaQueries, drp => drp.property === property);
      if (i >= 0 && !this.droplet.mediaQueries[i].selector) {
        this.$store.dispatch('spliceDropletVal', {
          index: i,
          array: this.droplet.mediaQueries,
        });
      }
    },
    isChanged() {
      // check to see if section has been changed from initial blank section
      const changes = [];
      _.forOwn(sectionDefaults, (value, key) => {
        if (
          this.droplet[key] !== value &&
          key !== 'errors' &&
          key !== 'sectionErrors' &&
          key !== 'colorPicker' &&
          key !== 'droplets'
        ) {
          changes.push(key);
        }
      });
      return changes.length > 0;
    },
    removeSectionCheck() {
      const blocks = this.$store.getters.getContentBlocks;
      if (blocks.length <= 2) {
        if (blocks.length <= 1) {
          // if only 1 sections
          this.toggleModal('Error');
        } else {
          // check if there is a content block AND a section
          _.forEach(blocks, (block) => {
            if (!_.isUndefined(block) && this.droplet !== block && block.type !== 'sectionBlock') {
              this.toggleModal('Error');
            } else if (
              !_.isUndefined(block) &&
              this.droplet !== block &&
              block.type === 'sectionBlock'
            ) {
              if (this.isChanged()) {
                this.toggleModal('Confirm');
              } else {
                this.removeSection();
              }
            }
          });
        }
        // if more than 2 sections, check if any settings have been altered
      } else if (this.isChanged()) {
        this.toggleModal('Confirm');
      } else {
        this.removeSection();
      }
    },
    removeSection() {
      // run remove droplet hooks
      this.removeDropletHook(this.droplet);
      const payload = {};
      payload.index = this.blockIndex;
      payload.items = this.items;
      // if confirm modal is open, close it then remove section
      if (this.showConfirmModal) {
        this.toggleModal('Confirm');
        // we utilize the timeout for smooth animation. we wait for the close modal effect
        // and DOM moving before we actually remove the section from the page.
        setTimeout(() => {
          this.$store.dispatch('removeContentBlock', payload);
          this.setEditHistory({
            type: 'remove_droplet',
            meta: {
              dropletType: this.droplet.type,
            },
          });
        }, 250);
      } else {
        this.$store.dispatch('removeContentBlock', payload);
        this.setEditHistory({
          type: 'remove_droplet',
          meta: {
            dropletType: this.droplet.type,
          },
        });
      }
    },
    setDragType(type) {
      window.drzzle.dragType = type;
    },
    promise(action, error) {
      return new Promise((resolve, reject) => {
        if (action) {
          resolve();
        } else {
          reject(Error(`Error in Section: ${error}`));
        }
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/section/index.js